"use client";

import React, { useState } from "react";
import Image from "next/image";
import MailChecker from "mailchecker";
import validator from "validator";
import moment from "moment";
import { FaCircleNotch } from "react-icons/fa";
import { useTranslations } from 'next-intl';
type NewsletterSectionProps = {
  logoSrc?: string;
  logoAlt?: string;
  tagline?: string;
};

/**
 * NewsletterSection
 * -------------------
 * Fullscreen black hero: logo, tagline, two underline-style inputs
 * (name / email), and a text-only "(SUBSCRIBE)" action — matching the
 * reference screenshot's minimal, type-driven layout.
 *
 * Submission logic mirrors the ShopletterDialog pattern you shared:
 * same endpoint shape, same header/token pattern, same email checks
 * (regex + validator + MailChecker for disposable addresses), plus a
 * simple required check for name since this form collects both.
 *
 * Note: this version uses plain English copy instead of next-intl's
 * `useTranslations`, since the translation keys for a name field /
 * this specific layout weren't given. Swap the literal strings below
 * for `t('...')` calls if you want it wired into your i18n setup.
 */
export default function NewsletterSection({
  logoSrc = "/logo.png",
  logoAlt = "Logo",
  tagline,
}: NewsletterSectionProps) {
  const t = useTranslations('ShopSection');
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState("");


  const isValidEmail = (
    value: string
  ): { valid: boolean; message?: string } => {
    if (!value || value.trim() === "") {
      return { valid: false, message: t('Email') };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return { valid: false, message: t('Invalid') };
    }

    if (!validator.isEmail(value)) {
      return { valid: false, message: t('Invalid') };
    }

    if (!MailChecker.isValid(value)) {
      return { valid: false, message: t('Disposable') };
    }

    return { valid: true };
  };

  const handleSubscribe = async () => {
    setError("");

    if (!name || name.trim() === "") {
      setError(t('Name'));
      return;
    }

    const emailValidation = isValidEmail(email);
    if (!emailValidation.valid) {
      setError(emailValidation.message || t('Invalid'));
      return;
    }

    setIsLoading(true);

    const now = moment();
    const date = now.format("MMMM Do YYYY");
    const time = now.format("h:mm:ss a");

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_URL}emailletterpost/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Token " + process.env.NEXT_PUBLIC_TOKEN,
          },
          body: JSON.stringify({
            name,
            email,
            date,
            time,
          }),
        }
      );

      const rawResponse = await response.text();

      if (!response.ok) {
        const errorData = rawResponse ? JSON.parse(rawResponse) : {};
        setError(errorData.message || t('Subscription'));
        return;
      }

      setIsSubscribed(true);
      setError("");
      setName("");
      setEmail("");
    } catch (err) {
      console.error("Error during subscription:", err);
      setError(t('error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoading && !isSubscribed) {
      handleSubscribe();
    }
  };

  return (
    <section className="flex h-[650px] w-full flex-col items-center justify-center bg-a px-6 py-20">
      {/* Logo */}
      <div className="relative mb-10 h-40 w-full max-w-xl sm:h-48">
        <Image
          src={logoSrc}
          alt={logoAlt}
          fill
          priority
          className="object-contain"
        />
      </div>

      {/* Tagline */}
      <p className="mb-10 bg-gradient-to-r from-yel via-gold to-white bg-clip-text text-xs font-semibold tracking-[0.2em] text-transparent sm:text-sm">
        {t('JOIN')}
      </p>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md text-center"
        noValidate
      >
        {isSubscribed ? (
          <p className="py-6 text-sm tracking-widest text-white/90">
            {t('LIST')}
          </p>
        ) : (
          <>
            <div className="mb-6">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('NAME')}
                disabled={isLoading}
                className="w-full border-b border-white/40 bg-transparent pb-3 text-center text-base tracking-wide text-white placeholder-white outline-none transition-colors focus:border-white disabled:opacity-50"
              />
            </div>

            <div className="mb-8">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('EMAIL')}
                disabled={isLoading}
                className="w-full border-b border-white/40 bg-transparent pb-3 text-center text-base tracking-wide text-white placeholder-white outline-none transition-colors focus:border-white disabled:opacity-50"
              />
            </div>

            {error && (
              <p className="mb-4 text-xs tracking-wide text-red-400">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center gap-2 text-sm tracking-widest text-white transition-opacity hover:opacity-70 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <FaCircleNotch className="animate-spin" />
                  {t('SUBSCRIBING')}
                </>
              ) : (
                t('SUBSCRIBE')
              )}
            </button>
          </>
        )}
      </form>
    </section>
  );
}